/**
 * OAuthCallback.jsx
 * Handles OAuth 2.0 callback after user authenticates with provider
 *
 * Flow:
 * 1. User clicks "Login with Zoho" → redirected to Zoho
 * 2. User logs in to Zoho
 * 3. Zoho redirects back to /auth/callback?code=XXX
 * 4. This component exchanges code for session
 * 5. Redirects to home page
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { catalystFetch } from '../services/catalystFetch';

const API_BASE = process.env.NODE_ENV === 'production'
  ? '/server/processMedia'
  : '';

export default function OAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    handleOAuthCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOAuthCallback = async () => {
    // Get auth code from URL params
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');

    // Handle OAuth errors
    if (errorParam) {
      setError(`Authentication failed: ${errorParam}`);
      setProcessing(false);
      setTimeout(() => navigate('/'), 3000);
      return;
    }

    // Validate code exists
    if (!code) {
      setError('No authorization code received');
      setProcessing(false);
      setTimeout(() => navigate('/'), 3000);
      return;
    }

    try {
      // Exchange code for session via backend
      const response = await catalystFetch(`${API_BASE}/auth/oauth/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Important for setting session cookie
        body: JSON.stringify({ code, state }),
      });

      if (!response.ok) {
        throw new Error('Failed to complete authentication');
      }

      const data = await response.json();

      if (data.success) {
        // Redirect to home page
        navigate('/', { replace: true });
      } else {
        throw new Error(data.error || 'Authentication failed');
      }
    } catch (err) {
      console.error('[OAuth] Callback error:', err);
      setError(err.message || 'Authentication failed');
      setProcessing(false);
      setTimeout(() => navigate('/'), 3000);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-canvas)' }}>
      <div className="rounded-lg shadow-lg p-8 max-w-md w-full text-center" style={{ background: 'var(--bg-surface)' }}>
        {processing && !error && (
          <>
            <div className="mb-4">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4" style={{ borderColor: 'var(--border-accent)', borderTopColor: 'var(--accent)' }}></div>
            </div>
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Completing authentication...
            </h2>
            <p style={{ color: 'var(--text-muted)' }}>
              Please wait while we log you in.
            </p>
          </>
        )}

        {error && (
          <>
            <div className="mb-4 text-4xl">❌</div>
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-danger)' }}>
              Authentication Failed
            </h2>
            <p className="mb-4" style={{ color: 'var(--text-muted)' }}>{error}</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Redirecting to home page...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
